import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { PaymentInstallments, AvailabilityStatusText, Contract, MembershipLevel, MembershipLevelPrices } from "@/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Re-export error handling utilities
export { formatErrorMessage, getFieldError } from "./errorHandling";


export function generateRandomMembershipNumber() {
  let result = '';
  const length = 8; // Length of the Owner ID
  for (let i = 0; i < length; i++) {
    result += Math.floor(Math.random() * 10).toString();
  }
  return parseInt(result, 10); // Convert to number before returning
};


export function generateYearRange() {
   const start = new Date().getFullYear();
  const years = [];
  for (let year = start; year <= start + 10; year++) {
    years.push(year);
  }
  return years;
}

export function getWeeks() {
  return Array.from({ length: 52 }, (_, i) => i + 1);
}

export const calculatePointPrice = (membershipLevel: string): number => {
  return MembershipLevelPrices[membershipLevel as MembershipLevel] || 0;
};

/**
 * Calculate membership level based on points and real estate ownership status
 * @param points - Number of points rights purchased (can be undefined/null)
 * @param isRealEstate - Whether the person is a real estate owner (can be undefined/null)
 * @returns The calculated membership level or null if parameters are not ready
 */
export const calculateMembershipLevel = (points: number | undefined | null, isRealEstate: boolean | undefined | null): MembershipLevel | null => {
  // Check that we have actual values (not undefined/null) for both required parameters
  const hasPointsRights = points !== undefined && points !== null && !isNaN(Number(points));
  const hasRealEstateStatus = isRealEstate !== undefined && isRealEstate !== null;
  
  // Return null if we don't have all required parameters
  if (!hasPointsRights || !hasRealEstateStatus) {
    return null;
  }

  // If the person is a real estate owner, they automatically get Crown level
  if (isRealEstate === true) {
    return MembershipLevel.HOC_Crown;
  }

  // Otherwise, calculate based on points
  const pointsNumber = Number(points) || 0;
  
  if (pointsNumber >= 3001) {
    return MembershipLevel.HOC_Emerald_Plus;
  } else if (pointsNumber >= 2001) {
    return MembershipLevel.HOC_Sapphire_Plus;
  } else {
    return MembershipLevel.HOC_Topaz_Plus;
  }
};

export const getStatusColor = (status: string | number, isFromWeeksApi: boolean = false): string => {
  // Handle numeric statuses from API (AvailabilityStatusText enum)
  if (typeof status === 'number') {
    switch (status) {
      case AvailabilityStatusText.Available: // AVAILABLE
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case AvailabilityStatusText.Reserved: // RESERVED
        return 'bg-yellow-100 text-yellow-800 cursor-not-allowed';
      case AvailabilityStatusText.Booked: // SOLD/BOOKED
        return 'bg-red-100 text-red-800 cursor-not-allowed';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  // Handle string statuses from bookings
  switch (status) {
    case 'available':
      return 'bg-green-100 text-green-800 hover:bg-green-200';
    case 'reserved':
      return 'bg-yellow-100 text-yellow-800 cursor-not-allowed';
    case 'sold':
      return 'bg-red-100 text-red-800 cursor-not-allowed';
    case 'new':
      return 'bg-blue-100 text-blue-800';
    case 'booked':
      return 'bg-blue-100 text-blue-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'released':
      return 'bg-red-100 text-red-800';
    case 'selected':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};




export function reduceYears(years: number[]): string[] {
  if (!years || !Array.isArray(years) || years.length === 0) return [];

  // Make sure we're working with numbers
  const yearsAsNumbers = years.map(year =>
    typeof year === 'string' ? parseInt(year, 10) : year
  ).filter(year => !isNaN(year));

  if (yearsAsNumbers.length === 0) return [];

  yearsAsNumbers.sort((a, b) => a - b);

  const ranges: string[] = [];
  let start = yearsAsNumbers[0];
  let end = yearsAsNumbers[0];

  for (let i = 1; i < yearsAsNumbers.length; i++) {
    if (yearsAsNumbers[i] === end + 1) {
      end = yearsAsNumbers[i];
    } else {
      if (start === end) {
        ranges.push(`${start}`);
      } else {
        ranges.push(`${start}-${end}`);
      }
      start = yearsAsNumbers[i];
      end = yearsAsNumbers[i];
    }
  }

  if (start === end) {
    ranges.push(`${start}`);
  } else {
    ranges.push(`${start}-${end}`);
  }

  return ranges;
}

export function multichoiceArray<T>(array: T[] | null | undefined): { Value: T }[] {
  if (!array || !Array.isArray(array)) return [];
  return array.map(item => ({ Value: item }));
}

export function formatPaymentDetails(paymentDetails: PaymentInstallments[]): string {
  return paymentDetails
    .map(detail => `GBP ${detail.amount.toFixed(2)} on ${new Date(detail.date).toLocaleDateString('en-GB')}`)
    .join('\n');
}

export function ucFirst(str: string | undefined | number): string {
  if (str === undefined || str === null) return '';

  // For numbers from availability status
  if (typeof str === 'number') {
    switch (str) {
      case AvailabilityStatusText.Available: // AVAILABLE
        return 'Available';
      case AvailabilityStatusText.Reserved: // RESERVED
        return 'Reserved';
      case AvailabilityStatusText.Booked: // SOLD/BOOKED
        return 'Booked';
      case AvailabilityStatusText.Selected: // Selected
        return 'Selected';
      default:
        return 'Unknown';
    }
  }

  // Convert to string first if it's a number
  const stringValue = String(str);

  if (stringValue.length === 0) return '';
  return stringValue.charAt(0).toUpperCase() + stringValue.slice(1).toLowerCase();
}

export function formatCurrency(
  amount: number,
  locale: string = 'en-GB',
  currency: string = 'GBP'
): string {
  // Ensure amount is a number
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Check if amount is a valid number
  if (isNaN(numericAmount)) {
    return '£0.00';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericAmount);
}

export const getAvailableYears = (years: number[], selectedWeek: number | null, selectedUnit: string | null, unitsMap: Record<string, any>): number[] => {
  if (!selectedWeek || !selectedUnit) return years;

  return years.filter(year => {
    const unit = unitsMap[selectedUnit];
    if (!unit || !unit.rows || !unit.rows.value) return false;
    const weekData = unit.rows.value.find(row => row.week === selectedWeek.toString());
    return weekData && weekData[year.toString()] !== "Sold";
  });
};

// Function to find available years for a specific week (up to maxYears)
export const findAvailableYearsForWeek = (
  week: number,
  roomNumber: string,
  unitAvailability: any[],
  bookings: any[],
  maxYears: number = 10
): number[] => {
  const allYears = generateYearRange().sort((a, b) => a - b);
  const availableYears: number[] = [];

  for (const year of allYears) {
    if (availableYears.length >= maxYears) break;

    const isBooked = bookings.some(booking =>
      booking.unitNumber === roomNumber &&
      booking.week === week &&
      booking.years.includes(year) &&
      (booking.status === 1 || booking.status === 'pending' || booking.status === 'booked')
    );

    if (isBooked) continue;

    const isAvailable = unitAvailability.some(a =>
      a && a.number === roomNumber && a.week === week && a.year === year &&
      a.status === AvailabilityStatusText.Available
    );

    if (isAvailable) {
      availableYears.push(year);
    }
  }

  return availableYears;
};


// Helper function to get Adobe status badge color
export const getAdobeStatusBadgeClass = (status: string | undefined) => {
  if (!status) return 'bg-gray-100 text-gray-800';

  switch (status.toLowerCase()) {
    case 'out_for_signature':
    case 'out_for_approval':
    case 'out_for_delivery':
    case 'out_for_acceptance':
    case 'out_for_form_filling':
      return 'bg-blue-100 text-blue-800';
    case 'waiting_for_others':
    case 'waiting_for_authoring':
    case 'waiting_for_me':
      return 'bg-yellow-100 text-yellow-800';
    case 'signed':
    case 'approved':
    case 'accepted':
    case 'delivered':
    case 'form_filled':
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
    case 'canceled':
    case 'declined':
    case 'rejected':
    case 'expired':
    case 'aborted':
      return 'bg-red-100 text-red-800';
    case 'archived':
      return 'bg-purple-100 text-purple-800';
    case 'draft':
    case 'authoring':
    case 'prefill':
      return 'bg-indigo-100 text-indigo-800';
    case 'in_process':
    case 'processing':
    case 'sending':
      return 'bg-cyan-100 text-cyan-800';
    case 'widget':
    case 'other':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
//   // Helper function to get Adobe status badge color
//   export const getAdobeStatusBadgeClass = (status: string | undefined) => {
//     if (!status) return 'bg-gray-100 text-gray-800';

//     switch(status.toLowerCase()) {
//       case 'out_for_signature':
//       case 'out_for_approval':
//       case 'out_for_delivery':
//       case 'out_for_acceptance':
//       case 'out_for_form_filling':
//         return 'bg-blue-100 text-blue-800';
//       case 'waiting_for_others':
//       case 'waiting_for_authoring':
//       case 'waiting_for_me':
//         return 'bg-yellow-100 text-yellow-800';
//       case 'signed':
//       case 'approved':
//       case 'accepted':
//       case 'delivered':
//       case 'form_filled':
//       case 'completed':
//         return 'bg-green-100 text-green-800';
//       case 'cancelled':
//       case 'canceled':
//       case 'declined':
//       case 'rejected':
//       case 'expired':
//       case 'aborted':
//         return 'bg-red-100 text-red-800';
//       case 'archived':
//         return 'bg-purple-100 text-purple-800';
//       case 'draft':
//       case 'authoring':
//       case 'prefill':
//         return 'bg-indigo-100 text-indigo-800';
//       case 'in_process':
//       case 'processing':
//       case 'sending':
//         return 'bg-cyan-100 text-cyan-800';
//       case 'widget':
//       case 'other':
//         return 'bg-orange-100 text-orange-800';
//       default:
//         return 'bg-gray-100 text-gray-800';
//     }
//   };



export const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'new': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'pending': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
    case 'active': return 'bg-slate-100 text-slate-800 border-slate-200';
    case 'complete': return 'bg-green-100 text-green-800 border-green-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};


/**
 * Determines if a contract needs review by an admin
 * This typically applies when a contract was created or modified by a manager
 * and needs approval from an admin before proceeding
 *
 * @param contract The contract to check
 * @returns boolean indicating if the contract needs review
 */
export const contractNeedsReview = (contract: Contract): boolean => {
  const updatedRole = contract.userHistory?.updated?.role?.toLowerCase();
  const createdRole = contract.userHistory?.created?.role?.toLowerCase();

  // If created by an admin or super_admin, it NEVER needs review.
  if (createdRole === 'admin' || createdRole === 'super_admin') {
    return false;
  }

  // Return false if the contract was updated by an admin or super_admin
  if (updatedRole === 'admin' || updatedRole === 'super_admin') {
    return false;
  }

  // Check if the contract has a created field with manager role
  if (createdRole === 'manager') {
    return true;
  }

  // Fallback: If in pending status and no admin update is registered
  if (contract.status === 'pending' && !updatedRole) {
    return true;
  }

  // Default case - doesn't need review
  return false;
};

/**
 * Gets the appropriate action text for a contract that needs review
 * based on user role and contract history
 *
 * @param contract The contract to get action text for
 * @param userRole The current user's role
 * @returns The action text to display
 */
export const getReviewActionText = (contract: Contract, userRole: string): string => {
  const role = userRole?.toLowerCase();

  // If user is not admin or super_admin, always show "Save"
  if (role !== 'admin' && role !== 'super_admin') {
    return "Save";
  }

  // If contract was created by a manager and needs review by admin
  if (contractNeedsReview(contract)) {
    return "Approve";
  }

  const updatedRole = contract.userHistory?.updated?.role?.toLowerCase();
  // If contract was previously updated by an admin or super_admin
  if (updatedRole === 'admin' || updatedRole === 'super_admin') {
    return "Save";
  }

  return "Save";
};



// Api helper

// Debug logger for requests
export const logRequest = (config: any) => {
  console.log('🚀 Request:', {
    url: config.url,
    method: config.method,
    headers: config.headers,
    data: config.data,
  });
  return config;
};

// Debug logger for responses
export const logResponse = (response: any) => {
  console.log('✅ Response:', {
    url: response.config?.url, // Include the URL of the request
    status: response.status,
    statusText: response.statusText,
    data: response.data,
    headers: response.headers,
  });
  return response;
};

// Debug logger for errors
export const logError = (error: any) => {
  console.error('❌ Error:', {
    message: error.message,
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
    headers: error.response?.headers,
  });
  return Promise.reject(error);
};